import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { AGREEMENT_SECTIONS, REPAIR_SCHEDULE } from "@/data/agreement";
import { DemoTag, EASE } from "@/components/common";

export default function AgreementModal({ open, onClose }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="modal-backdrop"
          data-testid="agreement-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          onClick={onClose}
        >
          <motion.div
            className="modal-panel max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.5, ease: EASE }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 md:px-10 py-6 border-b border-white/[0.1] shrink-0">
              <div>
                <p className="text-[0.6rem] tracking-[0.35em] uppercase text-zinc-500">Senna Prestige Travels</p>
                <h3 className="font-display text-xl md:text-2xl text-white mt-2">Vehicle Rental Agreement & Terms & Conditions</h3>
              </div>
              <div className="flex items-center gap-4">
                <DemoTag>Preview</DemoTag>
                <button data-testid="agreement-close-button" onClick={onClose} className="text-zinc-500 hover:text-white transition-colors" aria-label="Close">
                  <X size={20} strokeWidth={1.5} />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto px-6 md:px-10 py-8 space-y-10">
              {AGREEMENT_SECTIONS.map((s) => (
                <div key={s.title}>
                  <h4 className="text-[0.68rem] tracking-[0.3em] uppercase text-white mb-4">{s.title}</h4>
                  {s.body && s.body.map((p, i) => (
                    <p key={i} className="text-sm text-zinc-400 font-light leading-relaxed mb-3">{p}</p>
                  ))}
                  {s.terms && (
                    <div className="space-y-4">
                      {s.terms.map(([h, p]) => (
                        <div key={h}>
                          <p className="text-sm text-zinc-200 font-normal">{h}</p>
                          <p className="text-sm text-zinc-400 font-light leading-relaxed mt-1">{p}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              <div>
                <h4 className="text-[0.68rem] tracking-[0.3em] uppercase text-white mb-2">Section 9 — Vehicle Repair Cost Schedule</h4>
                <p className="text-sm text-zinc-400 font-light leading-relaxed mb-6">
                  Damage charges depend on the actual damage and are assessed against this schedule. Any deduction from the security deposit is reviewed and approved by the Owner.
                </p>
                <div className="space-y-8">
                  {REPAIR_SCHEDULE.map((g) => (
                    <div key={g.group}>
                      <p className="text-[0.62rem] tracking-[0.28em] uppercase text-zinc-200 mb-4">{g.group}</p>
                      {g.subgroups.map(([sub, rows]) => (
                        <div key={sub} className="mb-5">
                          <p className="text-[0.58rem] tracking-[0.24em] uppercase text-zinc-500 mb-2">{sub}</p>
                          <div className="divide-y divide-white/[0.06] border-y border-white/[0.06]">
                            {rows.map(([item, range]) => (
                              <div key={item} className="flex items-center justify-between gap-6 py-2.5">
                                <span className="text-sm text-zinc-400 font-light">{item}</span>
                                <span className="text-sm text-zinc-200 font-light whitespace-nowrap">{range}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="px-6 md:px-10 py-5 border-t border-white/[0.1] shrink-0 flex items-center justify-between">
              <span className="text-[0.58rem] tracking-[0.25em] uppercase text-zinc-500">Preview document — acceptance is recorded in the final system</span>
              <button data-testid="agreement-done-button" className="btn-ghost !py-3 !px-6" onClick={onClose}>Done</button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
